import { Plus, RefreshCw01, Trash01 } from '@untitledui/icons';
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state/empty-state';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { getPortfolioActionError } from '@/lib/portfolio/errors';
import { useCancelInvitation } from '@/lib/portfolio/hooks/useCancelInvitation';
import { useOrgInvitations } from '@/lib/portfolio/hooks/useOrgInvitations';
import { useResendInvitation } from '@/lib/portfolio/hooks/useResendInvitation';
import type { PortfolioInvitation } from '@/lib/portfolio/types';

import { InviteMemberModal } from './InviteMemberModal';

interface OrgInvitationsSectionProps {
  portfolioId: number;
  orgId: number;
  /** Display name of the target org — threaded to the invite modal title. */
  orgName: string;
}

interface InvitationRowProps {
  invitation: PortfolioInvitation;
  portfolioId: number;
  orgId: number;
}

function InvitationRow({ invitation, portfolioId, orgId }: InvitationRowProps) {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const cancelMutation = useCancelInvitation(portfolioId, orgId);
  const resendMutation = useResendInvitation(portfolioId, orgId);

  const handleResend = useCallback(() => {
    resendMutation.mutate({ invitationId: invitation.id });
  }, [resendMutation, invitation.id]);

  const handleOpenCancelDialog = useCallback(() => {
    setIsCancelDialogOpen(true);
  }, []);

  const handleCancelDialogClose = useCallback(() => {
    setIsCancelDialogOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    await cancelMutation.mutateAsync({ invitationId: invitation.id });
    setIsCancelDialogOpen(false);
  }, [cancelMutation, invitation.id]);

  // Resend has no dialog, so its error renders inline in the row. Cancel's
  // error is rendered inside the confirmation dialog (below) — the dialog stays
  // open on failure and covers the row, so a row-level message would be hidden.
  const resendError = resendMutation.isError
    ? getPortfolioActionError(resendMutation.error)
    : null;
  const cancelError = cancelMutation.isError
    ? getPortfolioActionError(cancelMutation.error)
    : null;

  return (
    <div className='flex flex-col gap-1 py-2'>
      <div className='flex items-center justify-between gap-2'>
        <div>
          <p className='text-body-small text-[var(--color-text-primary)]'>
            {invitation.email}
          </p>
          <p className='text-body-small text-[var(--color-text-tertiary)]'>
            {invitation.role_name}
          </p>
        </div>
        <div className='flex items-center gap-2'>
          {resendMutation.isSuccess && (
            <span className='text-body-small text-[var(--color-text-success-primary)]'>
              Resent
            </span>
          )}
          <Button
            color='tertiary'
            size='sm'
            iconLeading={RefreshCw01}
            aria-label={`Resend invitation to ${invitation.email}`}
            isDisabled={resendMutation.isPending}
            isLoading={resendMutation.isPending}
            onClick={handleResend}
          />
          <Button
            color='tertiary'
            size='sm'
            iconLeading={Trash01}
            aria-label={`Cancel invitation to ${invitation.email}`}
            isDisabled={cancelMutation.isPending}
            onClick={handleOpenCancelDialog}
          />
        </div>
      </div>
      {resendError && (
        <p
          role='alert'
          className='text-body-small text-[var(--color-text-error-primary)]'
        >
          {resendError}
        </p>
      )}
      <ConfirmationDialog
        isOpen={isCancelDialogOpen}
        variant='destructive'
        title='Cancel invitation?'
        confirmLabel='Cancel invitation'
        instance='cancel-invitation'
        isLoading={cancelMutation.isPending}
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelDialogClose}
      >
        {cancelError && (
          <p
            role='alert'
            className='text-body-small text-[var(--color-text-error-primary)]'
          >
            {cancelError}
          </p>
        )}
      </ConfirmationDialog>
    </div>
  );
}

export function OrgInvitationsSection({
  portfolioId,
  orgId,
  orgName,
}: OrgInvitationsSectionProps) {
  const invitationsQuery = useOrgInvitations(portfolioId, orgId);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const handleOpenInviteModal = useCallback(() => {
    setIsInviteModalOpen(true);
  }, []);

  const { refetch: refetchInvitations } = invitationsQuery;
  const retryAction = useMemo(
    () => ({
      label: 'Retry',
      icon: RefreshCw01,
      onClick: () => {
        void refetchInvitations();
      },
    }),
    [refetchInvitations]
  );

  const invitations = invitationsQuery.data ?? [];

  let body: ReactNode;
  if (invitationsQuery.isLoading) {
    body = <LoadingIndicator size='sm' />;
  } else if (invitationsQuery.isError) {
    body = (
      <EmptyState
        variant='default'
        title="Couldn't load invitations"
        action={retryAction}
      />
    );
  } else if (invitations.length === 0) {
    body = <EmptyState variant='empty' title='No pending invitations' />;
  } else {
    body = (
      <div className='divide-y divide-[var(--color-border-secondary)]'>
        {invitations.map(invitation => (
          <InvitationRow
            key={invitation.id}
            invitation={invitation}
            portfolioId={portfolioId}
            orgId={orgId}
          />
        ))}
      </div>
    );
  }

  return (
    <div className='mt-4'>
      <div className='mb-3 flex items-center justify-between'>
        <h4 className='text-body font-semibold text-[var(--color-text-primary)]'>
          Pending invitations
        </h4>
        <Button
          color='secondary'
          size='sm'
          iconLeading={Plus}
          onClick={handleOpenInviteModal}
        >
          Invite member
        </Button>
      </div>
      {body}
      <InviteMemberModal
        portfolioId={portfolioId}
        orgId={orgId}
        orgName={orgName}
        isOpen={isInviteModalOpen}
        onOpenChange={setIsInviteModalOpen}
      />
    </div>
  );
}
